import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, ShieldCheck } from "lucide-react";
import { Section } from "@/components/Section";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  approve_invoice: "Approve invoice",
  approve_vendor_application: "Approve vendor",
  resolve_clarification: "Resolve clarification",
};

export default function QuickAction() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [action, setAction] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/quick-action/${token}`);
        setAction(r.data);
        setState("ready");
      } catch (e) {
        setError(e?.response?.data?.detail || "This link is no longer valid.");
        setState("error");
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    try {
      setConfirming(true);
      const r = await axios.post(`${BACKEND_URL}/api/quick-action/${token}/confirm`);
      setResult(r.data);
      setState("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not complete this action.");
      setState("error");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Section className="pt-32" testId="quick-action-page">
      <div className="max-w-md mx-auto rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        {state === "loading" && (
          <div className="text-center">
            <Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" />
            <p className="mt-3 text-sm text-slate-500">Loading action…</p>
          </div>
        )}
        {state === "ready" && action && (
          <div data-testid="quick-action-confirm">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-700 mb-3">
              Quick action
            </div>
            <h1 className="font-display text-2xl font-extrabold tracking-tighter text-slate-900 leading-tight">
              {ACTION_LABELS[action.action_type] || action.label}
            </h1>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">{action.description}</p>
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11px] text-slate-500 flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                This is a single-use link sent to your inbox. It will expire after one click.
              </span>
            </div>
            <button
              type="button"
              disabled={confirming}
              onClick={confirm}
              data-testid="quick-action-confirm-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 py-3.5 text-sm font-bold disabled:opacity-50"
            >
              {confirming ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Confirming…
                </>
              ) : (
                <>
                  Confirm — {action.label} <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
        {state === "done" && result && (
          <div className="text-center" data-testid="quick-action-done">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Done.</h1>
            <p className="mt-2 text-sm text-slate-600">{result.message}</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-900"
            >
              Return to nodera.health <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
        {state === "error" && (
          <div className="text-center" data-testid="quick-action-error">
            <div className="w-12 h-12 mx-auto rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-700">
              <AlertCircle className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Link is no longer valid.</h1>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
          </div>
        )}
      </div>
    </Section>
  );
}
